
import React, { useState, useEffect } from 'react';
import { useMovilidadElectrica } from '../../context/MovilidadElectricaContext';
import { 
  Typography, 
  Box, 
  Chip, 
  Alert, 
  useMediaQuery, 
  useTheme,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  CardActions,
  Button,
  Pagination,
  Stack,
  Fade,
  CircularProgress,
  TablePagination,
  IconButton,
  Tooltip
} from '@mui/material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarCheck,
  faMapMarkerAlt,
  faClock,
  faIdCard,
  faChargingStation,
  faInfoCircle,
  faEye,
  faTrash,
  faBolt
} from '@fortawesome/free-solid-svg-icons';
import CancelBookingModal from './CancelBookingModal';

const formatBookingPeriodTo12Hour = (period) => {
  if (!period) return '';
  try {
    const dateTimeRegex = /(\d{4}-\d{2}-\d{2})\s(\d{2}):\d{2}:\d{2}\s-\s\d{4}-\d{2}-\d{2}\s(\d{2}):\d{2}:\d{2}/;
    const match = period.match(dateTimeRegex);
    if (!match) return period;
    const [, startTime, endTime] = match;
    const formatHour = (hour) => {
      const h = parseInt(hour, 10);
      const ampm = h < 12 ? 'AM' : 'PM';
      const formattedHour = h % 12 || 12;
      return `${formattedHour}:00 ${ampm}`;
    };
    return `${formatHour(startTime)} - ${formatHour(endTime)}`;
  } catch (e) {
    console.error("Error formatting booking period:", e);
    return period;
  }
};

const formatDate = (period) => {
  if (!period) return '';
  // Espera formato 'YYYY-MM-DD HH ...'
  return period.split(' ')[0];
};

const ReservaRow = ({ reserva, onView, onDelete }) => {
  const now = new Date();
  const bookingDate = new Date(reserva.start_date);
  const diff = bookingDate - now;
  const hours = diff / (1000 * 60 * 60);
  const canDelete = diff > 0;

  return (
    <TableRow
      hover
      sx={{
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: 'rgba(0, 38, 66, 0.02)',
          transform: 'scale(1.001)'
        }
      }}
    >
      <TableCell sx={{
        color: '#002642',
        fontWeight: 500,
        fontSize: '0.875rem'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FontAwesomeIcon
            icon={faIdCard}
            style={{ color: '#DB9600', fontSize: '0.8rem' }}
          />
          {reserva.plate}
        </Box>
      </TableCell>
      <TableCell sx={{
        color: '#75787B',
        fontSize: '0.875rem'
      }}>
        {reserva.matricula}
      </TableCell>
      <TableCell sx={{
        color: '#75787B',
        fontSize: '0.875rem'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FontAwesomeIcon
            icon={faMapMarkerAlt}
            style={{ color: '#DB9600', fontSize: '0.8rem' }}
          />
          {reserva.location}
        </Box>
      </TableCell>
      <TableCell sx={{
        color: '#75787B',
        fontSize: '0.875rem'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FontAwesomeIcon
            icon={faCalendarCheck}
            style={{ color: '#DB9600', fontSize: '0.8rem' }}
          />
          {formatDate(reserva.booking_period)}
        </Box>
      </TableCell>
      <TableCell sx={{
        color: '#75787B',
        fontSize: '0.875rem'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FontAwesomeIcon
            icon={faClock}
            style={{ color: '#DB9600', fontSize: '0.8rem' }}
          />
          {formatBookingPeriodTo12Hour(reserva.booking_period)}
        </Box>
      </TableCell>
      <TableCell sx={{ color: '#75787B', fontSize: '0.875rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FontAwesomeIcon
            icon={faBolt}
            style={{ color: '#DB9600', fontSize: '0.8rem' }}
          />
          {reserva.tariff ? `$${new Intl.NumberFormat('es-CO').format(reserva.tariff.price_per_kwh)}` : 'N/A'}
        </Box>
      </TableCell>
      <TableCell sx={{ color: '#75787B', fontSize: '0.875rem' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FontAwesomeIcon
            icon={faBolt}
            style={{ color: '#DB9600', fontSize: '0.8rem' }}
          />
          {reserva.kwh_consumed !== null && reserva.kwh_consumed !== undefined ? `${reserva.kwh_consumed.toFixed(2)} kWh` : 'N/A'}
        </Box>
      </TableCell>
      <TableCell align="right">
        <Tooltip title="Ver detalles" arrow>
          <IconButton
            onClick={(e) => { e.stopPropagation(); onView(reserva.booking_id); }}
            sx={{
              color: '#002642',
              backgroundColor: 'rgba(0, 38, 66, 0.04)',
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: '#002642',
                color: 'white',
                transform: 'scale(1.05)'
              }
            }}
          >
            <FontAwesomeIcon icon={faEye} />
          </IconButton>
        </Tooltip>
        <Tooltip title={canDelete ? "Cancelar reserva" : "No se puede cancelar la reserva"} arrow>
          <span>
            <IconButton
              onClick={(e) => { e.stopPropagation(); onDelete(reserva); }}
              disabled={!canDelete}
              sx={{
                color: canDelete ? '#d32f2f' : '#bdbdbd',
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
                borderRadius: 2,
                transition: 'all 0.2s ease-in-out',
                marginLeft: 1,
                '&:hover': {
                  backgroundColor: canDelete ? '#d32f2f' : 'rgba(211, 47, 47, 0.04)',
                  color: canDelete ? 'white' : '#bdbdbd',
                  transform: 'scale(1.05)'
                }
              }}
            >
              <FontAwesomeIcon icon={faTrash} />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

const ReservaCardMobile = ({ reserva, onView, onDelete }) => {
  const now = new Date();
  const bookingDate = new Date(reserva.start_date);
  const diff = bookingDate - now;
  const hours = diff / (1000 * 60 * 60);
  const canDelete = diff > 0;

  return (
    <Fade in timeout={600}>
      <Card
        sx={{
          mb: 2.5,
          borderRadius: 3,
          boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
          border: '1px solid rgba(0, 38, 66, 0.06)',
          cursor: 'pointer',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflow: 'hidden',
          '&:hover': {
            boxShadow: '0 8px 32px rgba(0, 38, 66, 0.12)',
            transform: 'translateY(-2px)',
          }
        }}
        onClick={() => onView(reserva.booking_id)}
      >
        <CardContent sx={{ p: 2.5 }}>
          {/* Header */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2.5
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: 2,
                  background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <FontAwesomeIcon
                  icon={faChargingStation}
                  style={{ color: '#FFFFFF', fontSize: '1rem' }}
                />
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: '#002642',
                  fontWeight: 600,
                  fontSize: '1rem'
                }}
              >
                Reserva #{reserva.booking_id}
              </Typography>
            </Box>

            <IconButton
              onClick={(e) => { e.stopPropagation(); onView(reserva.booking_id); }}
              sx={{
                color: '#002642',
                '&:hover': {
                  backgroundColor: 'rgba(0, 38, 66, 0.04)'
                }
              }}
            >
              <FontAwesomeIcon icon={faEye} />
            </IconButton>
          </Box>

          {/* Información en grid */}
          <Box sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: '1fr'
          }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 38, 66, 0.02)',
              border: '1px solid rgba(0, 38, 66, 0.06)'
            }}>
              <FontAwesomeIcon
                icon={faIdCard}
                style={{
                  marginRight: '12px',
                  color: '#DB9600',
                  fontSize: '0.9rem'
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#75787B',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25
                  }}
                >
                  Vehículo
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#002642',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {reserva.plate}
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 38, 66, 0.02)',
              border: '1px solid rgba(0, 38, 66, 0.06)'
            }}>
              <FontAwesomeIcon
                icon={faMapMarkerAlt}
                style={{
                  marginRight: '12px',
                  color: '#DB9600',
                  fontSize: '0.9rem'
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#75787B',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25
                  }}
                >
                  Ubicación
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#002642',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {reserva.location}
                </Typography>
              </Box>
            </Box>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 1.5
            }}>
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 38, 66, 0.02)',
                border: '1px solid rgba(0, 38, 66, 0.06)'
              }}>
                <FontAwesomeIcon
                  icon={faCalendarCheck}
                  style={{
                    color: '#DB9600',
                    fontSize: '0.9rem',
                    marginBottom: '8px'
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#75787B',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25,
                    textAlign: 'center'
                  }}
                >
                  Fecha
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#002642',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    textAlign: 'center'
                  }}
                >
                  {formatDate(reserva.booking_period)}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 38, 66, 0.02)',
                border: '1px solid rgba(0, 38, 66, 0.06)'
              }}>
                <FontAwesomeIcon
                  icon={faClock}
                  style={{
                    color: '#DB9600',
                    fontSize: '0.9rem',
                    marginBottom: '8px'
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#75787B',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25,
                    textAlign: 'center'
                  }}
                >
                  Turno
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#002642',
                    fontWeight: 500,
                    fontSize: '0.8rem',
                    textAlign: 'center'
                  }}
                >
                  {formatBookingPeriodTo12Hour(reserva.booking_period)}
                </Typography>
              </Box>
            </Box>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 38, 66, 0.02)',
              border: '1px solid rgba(0, 38, 66, 0.06)'
            }}>
              <FontAwesomeIcon
                icon={faBolt}
                style={{
                  marginRight: '12px',
                  color: '#DB9600',
                  fontSize: '0.9rem'
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#75787B',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25
                  }}
                >
                  Precio kWh
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#002642',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {reserva.tariff ? `$${new Intl.NumberFormat('es-CO').format(reserva.tariff.price_per_kwh)}` : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={{
              display: 'flex',
              alignItems: 'center',
              p: 1.5,
              borderRadius: 2,
              backgroundColor: 'rgba(0, 38, 66, 0.02)',
              border: '1px solid rgba(0, 38, 66, 0.06)'
            }}>
              <FontAwesomeIcon
                icon={faBolt}
                style={{
                  marginRight: '12px',
                  color: '#DB9600',
                  fontSize: '0.9rem'
                }}
              />
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#75787B',
                    fontSize: '0.7rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 0.25
                  }}
                >
                  Carga Entregada
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#002642',
                    fontWeight: 500,
                    fontSize: '0.875rem'
                  }}
                >
                  {reserva.kwh_consumed !== null && reserva.kwh_consumed !== undefined ? `${reserva.kwh_consumed.toFixed(2)} kWh` : 'N/A'}
                </Typography>
              </Box>
            </Box>
        </CardContent>
        <CardActions sx={{ justifyContent: 'flex-end', p: 2 }}>
          <Button
            onClick={(e) => { e.stopPropagation(); onDelete(reserva); }}
            disabled={!canDelete}
            variant="contained"
            color="error"
            startIcon={<FontAwesomeIcon icon={faTrash} />}
          >
            Cancelar
          </Button>
        </CardActions>
      </Card>
    </Fade>
  );
};


const MisReservasList = () => {
  const navigate = useNavigate();
  const { bookings, refreshBookings, loading, error, deleteBooking } = useMovilidadElectrica();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [notification, setNotification] = useState({ message: '', type: '' });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  useEffect(() => {
    refreshBookings();
  }, [refreshBookings]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRowClick = (bookingId) => {
    navigate(`/movilidadElectrica/reserva/${bookingId}`);
  };

  const handleDelete = (booking) => {
    setSelectedBooking(booking);
    setIsCancelModalOpen(true);
  };

  const handleCloseCancelModal = (shouldRefresh) => {
    setIsCancelModalOpen(false);
    setSelectedBooking(null);
    if (shouldRefresh) {
      refreshBookings();
    }
  };


  if (loading) {
    return (
      <Fade in timeout={600}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
          gap: 2
        }}>
          <CircularProgress
            size={60}
            sx={{ color: '#002642' }}
          />
          <Typography
            variant="body1"
            sx={{
              color: '#75787B',
              textAlign: 'center'
            }}
          >
            Cargando reservas...
          </Typography>
        </Box>
      </Fade>
    );
  }

  if (error) {
    return (
      <Fade in timeout={600}>
        <Alert 
          severity="error"
          sx={{
            borderRadius: 3,
            border: '1px solid rgba(211, 47, 47, 0.2)',
            '& .MuiAlert-icon': {
              color: '#d32f2f'
            }
          }}
        >
          Error al cargar las reservas: {error.message}
        </Alert>
      </Fade>
    );
  }

  const reservas = bookings.data || [];
  const pagedReservas = reservas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (reservas.length === 0) {
    return (
      <Fade in timeout={600}>
        <Box sx={{ 
          py: 8, 
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3
        }}>
          <Box
            sx={{
              p: 3,
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(0, 38, 66, 0.05) 0%, rgba(0, 87, 122, 0.05) 100%)',
              border: '2px dashed rgba(0, 38, 66, 0.1)'
            }}
          >
            <FontAwesomeIcon 
              icon={faChargingStation} 
              style={{ 
                color: '#75787B', 
                fontSize: '3rem'
              }}
            />
          </Box>
          <Box>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#002642',
                fontWeight: 600,
                mb: 1
              }}
            >
              No tienes reservas registradas
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: '#75787B',
                maxWidth: 400,
                mx: 'auto'
              }}
            >
              Cuando realices tu primera reserva de carga eléctrica, aparecerá aquí en tu historial.
            </Typography>
          </Box>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in timeout={800}>
      <Box>
       {notification.message && <Alert severity={notification.type} sx={{ mb: 2 }}>{notification.message}</Alert>}
        {!isMobile && (
          <TableContainer
            component={Paper} 
            sx={{
              borderRadius: 3,
              boxShadow: '0 2px 12px rgba(0, 38, 66, 0.08)',
              border: '1px solid rgba(0, 38, 66, 0.06)',
              overflow: 'hidden',
              mb: 3
            }}
          >
            <Table aria-label="Mis Reservas Table">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0, 38, 66, 0.02)' }}>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Placa
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Inmueble
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Celda Reservada
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Fecha
                  </TableCell>
                  <TableCell sx={{ 
                    fontWeight: 600, 
                    color: '#002642',
                    fontSize: '0.875rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Turno
                  </TableCell>
                  <TableCell sx={{
                   fontWeight: 600,
                   color: '#002642',
                   fontSize: '0.875rem',
                   textTransform: 'uppercase',
                   letterSpacing: '0.5px'
                 }}>
                   Precio kWh
                 </TableCell>
                  <TableCell sx={{
                   fontWeight: 600,
                   color: '#002642',
                   fontSize: '0.875rem',
                   textTransform: 'uppercase',
                   letterSpacing: '0.5px'
                 }}>
                   Carga Entregada
                 </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: 600,
                      color: '#002642',
                      fontSize: '0.875rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}
                  >
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedReservas.map((reserva) => (
                  <ReservaRow key={reserva.booking_id} reserva={reserva} onView={handleRowClick} onDelete={handleDelete} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {isMobile && (
          <Box sx={{ mb: 3 }}>
            {pagedReservas.map((reserva) => (
              <ReservaCardMobile key={reserva.booking_id} reserva={reserva} onView={handleRowClick} onDelete={handleDelete} />
            ))}
          </Box>
        )}

        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          mt: { xs: 2, sm: 2.5, md: 3 },
          '& .MuiTablePagination-root': {
            border: '1px solid rgba(0, 38, 66, 0.06)',
            borderRadius: 3,
            backgroundColor: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(0, 38, 66, 0.04)',
            width: '100%',
            maxWidth: { xs: '100%', sm: 'none' }
          }
        }}>
          <TablePagination
            component="div"
            count={reservas.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 15]}
            labelRowsPerPage={isMobile ? "Por página:" : "Filas por página:"}
            labelDisplayedRows={({ from, to, count }) =>
              isMobile ? `${from}-${to} de ${count}` : `${from}-${to} de ${count} reservas`
            }
            sx={{
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                color: '#002642',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              },
              '& .MuiTablePagination-select': {
                color: '#002642',
                fontSize: { xs: '0.8rem', sm: '0.875rem' }
              },
              '& .MuiIconButton-root': {
                color: '#002642',
                padding: { xs: '8px', sm: '12px' },
                '&:hover': {
                  backgroundColor: 'rgba(0, 38, 66, 0.04)'
                }
              },
              '& .MuiTablePagination-toolbar': {
                minHeight: { xs: '52px', sm: '64px' },
                paddingLeft: { xs: '8px', sm: '16px' },
                paddingRight: { xs: '8px', sm: '16px' }
              }
            }}
          />
        </Box>
        {selectedBooking && (
          <CancelBookingModal
            isOpen={isCancelModalOpen}
            onClose={handleCloseCancelModal}
            booking={selectedBooking}
            setNotification={setNotification}
          />
        )}
      </Box>
    </Fade>
  );
};

export default MisReservasList;
